import SEO from '../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { Download, Play, CheckCircle2, Users, Target, Calendar, Zap, Shield, Award, TrendingUp, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCTA from '../components/ProductCTA';

const ImplementationPage = () => {
  const stats = [
    { value: "100%", label: "Dedicated to SAP Business One" },
    { value: "4.9/5", label: "Customer Satisfaction" },
    { value: "Weeks", label: "Not Months" },
    { value: "15+", label: "Years Experience" }
  ];

  const approach = [
    { icon: Users, title: "Discovery & Design", strapline: "We listen, we understand", desc: "Deep dive into your unique business needs, processes, and goals. We design a solution that fits you perfectly, not a one-size-fits-all template." },
    { icon: Target, title: "Tailored Configuration", strapline: "Built for your business", desc: "Configure SAP Business One specifically for your workflows, industry requirements, and growth plans. Every setting optimised for your success." },
    { icon: Calendar, title: "Phased Deployment", strapline: "Minimal disruption", desc: "Carefully planned rollout in manageable phases. Your business keeps running smoothly while we transform it from within." },
    { icon: Zap, title: "Comprehensive Training", strapline: "Your team empowered", desc: "Hands-on training ensuring every team member is confident and productive from day one. We don't just install software - we transform your team." },
    { icon: Shield, title: "Go-Live Support", strapline: "We're right beside you", desc: "Intensive support during go-live and beyond. We'll never leave you - our partnership continues long after implementation." },
    { icon: TrendingUp, title: "Continuous Optimisation", strapline: "Always improving", desc: "Regular reviews and optimisations ensure you're getting maximum value. We're invested in your ongoing success." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="SAP Business One Implementation Services UK | Expert Deployment"
        description="Tailored SAP B1 implementation by Premier Partner. 8-12 week deployment, comprehensive training, post-go-live support. Your success is our mission."
        keywords="SAP B1 implementation, ERP deployment, SAP Business One setup, UK implementation partner, ERP project"
        canonical="https://www.ascarii.com/services/implementation"
      />

      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-blue-700 via-sky-800 to-blue-900">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1616587656977-ac36a5a430bc?w=1920&q=85" alt="Partnership" className="w-full h-full object-cover opacity-30" loading="eager" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">SAP Business One Implementation</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">Your Success is Our Mission</h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">We don't just implement software - we transform your business. Expert guidance, proven methodology, and unwavering support from day one to long-term success.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Partnership Approach</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">We're with you every step of the journey - from initial planning to long-term success</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {approach.map((step, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true, amount: 0.2 }} className="group p-8 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-lg transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-sky-500 group-hover:to-blue-500 transition-all">
                    <step.icon className="w-8 h-8 text-sky-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-sky-600 transition-colors">{step.title}</h3>
                    <p className="text-sm text-sky-600 font-medium mb-2">{step.strapline}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-12 border-2 border-blue-200">
              <div className="text-center">
                <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">Customer Success</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Abbeydale Direct</h2>
                <p className="text-lg text-gray-700 italic mb-4">
                  Ascarii works with us as though they are an extension of our team. It's like having an in-house SAP team working in the next office. Ascarii and SAP have taken our business from small thinking to making all things not only possible, but a reality.
                </p>
                <p className="text-sm text-gray-600">- Managing Director</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Matching SAP B1 Showcase */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed">
              Let's discuss how SAP Business One can help you achieve your goals with expert implementation.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <a href="/contact" className="inline-flex items-center justify-center rounded-xl bg-white text-blue-600 px-10 py-5 text-xl font-bold hover:bg-blue-50 shadow-2xl transition-all hover:scale-105">
                Get in Touch Today
              </a>
              <a href="/experience" className="inline-flex items-center justify-center rounded-xl border-3 border-white text-white px-10 py-5 text-xl font-bold hover:bg-white/10 shadow-2xl transition-all hover:scale-105">
                Discover The Ascarii Experience
              </a>
            </div>
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">SAP Premier Partner</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">Proven Methodology</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">4.9/5 Rated Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImplementationPage;
